import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faBullhorn, faPlus } from "@fortawesome/free-solid-svg-icons";
import { toSignal, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BehaviorSubject, catchError, map, of, switchMap } from "rxjs";

import { CallForOpportunityService } from "../../../api/services/call-for-opportunity.service";
import { CallForOpportunity } from "../../../api/models/call-for-opportunity";
import { StudentProfile } from "../../../api/models/student-profile";
import { OwnAnnouncementCardComponent } from "./own-announcement-card.component";
import { StudentAnnouncementService } from "../../../shared/services/student-announcement.service";
import { sortAnnouncementsNewestFirst } from "../../../shared/utils/call-for-opportunity.utils";
import { ProfileTabCardComponent } from "./profile-tab-card.component";

/**
 * Student's own announcements ("oproepen") tab — lists active calls for
 * opportunity in a grid, with a sober header and a friendly empty state.
 * Activated via the profile tab registry for the Student role only.
 *
 * Wraps its content in the shared <app-profile-tab-card> shell so the header
 * (icon + title + count + action) stays consistent with the other student
 * profile tabs.
 *
 * "New announcement" CTAs delegate to the shared StudentAnnouncementService
 * (same flow used by student-top-panel-actions). After a successful create
 * the service emits `created$`; the tab subscribes and re-fetches its list.
 *
 * @see User story #18091
 */
@Component({
  selector: "app-student-oproepen-tab",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    FontAwesomeModule,
    OwnAnnouncementCardComponent,
    ProfileTabCardComponent,
  ],
  template: `
    <app-profile-tab-card
      title="My announcements"
      [icon]="faBullhorn"
      [count]="count()"
      testId="tab-root"
    >
      <button
        tabAction
        type="button"
        data-testid="create-cta"
        class="inline-flex items-center gap-1.5 text-sm font-medium text-mty-accent hover:text-mty-body transition-colors"
        (click)="openAnnouncementModal()"
      >
        <fa-icon [icon]="faPlus" class="text-[10px]"></fa-icon>
        <span>{{ "New announcement" | translate }}</span>
      </button>

      @if (oproepen() === null) {
        <!-- Loading: deliberately empty; profile-shell handles top-level skeleton -->
      } @else if (oproepen()!.length === 0) {
        <div
          data-testid="empty-state"
          class="text-center py-8 px-4 text-sm text-mty-body/60"
        >
          <p class="mb-1 text-mty-body/80">
            {{ "No announcements yet" | translate }}
          </p>
          <p class="max-w-md mx-auto mb-4">
            {{
              "Post an announcement to let companies know what you are looking for."
                | translate
            }}
          </p>
          <button
            type="button"
            data-testid="empty-cta"
            class="inline-flex items-center gap-1.5 text-sm font-medium text-mty-accent hover:text-mty-body transition-colors"
            (click)="openAnnouncementModal()"
          >
            <fa-icon [icon]="faPlus" class="text-[10px]"></fa-icon>
            <span>{{ "Post your first announcement" | translate }}</span>
          </button>
        </div>
      } @else {
        <div
          data-testid="oproepen-grid"
          class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
        >
          @for (oproep of oproepen(); track oproep.id) {
            <div data-testid="oproep-card" class="flex">
              <app-own-announcement-card [data]="oproep" />
            </div>
          }
        </div>
      }
    </app-profile-tab-card>
  `,
})
export class StudentOproepenTabComponent {
  private readonly cfoService = inject(CallForOpportunityService);
  private readonly announcementService = inject(StudentAnnouncementService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly faBullhorn = faBullhorn;
  protected readonly faPlus = faPlus;

  /** Required for the create-modal flow (needs profile id + completion check). */
  entity = input.required<StudentProfile>();

  /**
   * Trigger that re-fetches the oproepen list. We push a new value after the
   * shared announcement service reports a successful create so the grid
   * reflects the new card immediately, without forcing a full profile reload.
   */
  private readonly refresh$ = new BehaviorSubject<void>(undefined);

  constructor() {
    this.announcementService.created$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refresh$.next());
  }

  /**
   * `null` = loading (service hasn't emitted yet); `[]` = empty state;
   * `T[]` with entries = grid render. The three states drive separate
   * template branches.
   */
  protected readonly oproepen = toSignal<CallForOpportunity[] | null>(
    this.refresh$.pipe(
      switchMap(() => this.cfoService.getCallForOpportunities()),
      // Endpoint returns a paged wrapper { data, totalItems, ... }; take the rows.
      map((res) =>
        sortAnnouncementsNewestFirst((res.data ?? []) as CallForOpportunity[]),
      ),
      catchError(() => of<CallForOpportunity[]>([])),
    ),
    { initialValue: null },
  );

  /** Exposed for consumers (e.g. a top-panel badge linking to this tab). */
  readonly count = computed(() => this.oproepen()?.length ?? 0);

  openAnnouncementModal(): void {
    this.announcementService.open(this.entity());
  }
}
